/* eslint-disable max-lines-per-function */
import type {FormEventHandler, RefObject} from 'react';
import {useContext, useEffect, useRef} from 'react';

import {action} from 'mobx';
import {useLocalObservable} from 'mobx-react-lite';

import {FormsDataContext} from '../contexts';

import type {FormField, FormState, FormValue, HandleFormSubmit, ValidationFunc} from '../sharedTypes/formHelperTypes';

/**
 * A custom React hook that provides comprehensive form management functionality including field registration, validation, submission handling, and state management.
 * This hook serves as the core internal form controller, managing form state through MobX observables and providing methods for field manipulation, validation, and form lifecycle events. It integrates with a global form context to enable cross-component form data sharing and provides both local and global form state management capabilities.
 * The hook automatically handles form field registration, validation execution, error management, and provides optimized form submission and reset functionality.
 *
 * @param id       The unique identifier for the form instance, used for global form data registration and tracking.
 * @param onChange An optional callback function that is executed whenever a form field value changes, receiving the field name and new value.
 * @param onError  A callback function that is executed when form validation fails during submission, allowing for custom error handling logic.
 * @param onReset  A form event handler that is called when the form is reset, providing custom reset behavior alongside the default field value restoration.
 * @param onSubmit A callback function that handles successful form submissions, receiving form context, values, and a FormData getter function.
 * @returns An object containing the form reference, form state management object, and event handlers for reset and submit operations.
 *
 * @example
 * ```tsx
 * const MyFormComponent = () => {
 *   const { form, formState, handleReset, handleSubmit } = useInternalForm(
 *     'user-profile-form',
 *     (name, value) => console.log(`Field ${name} changed to:`, value),
 *     () => alert('Form has validation errors!'),
 *     (e) => console.log('Form reset'),
 *     async ({ values, ctx }) => {
 *       console.log('Submitting:', values);
 *       // Handle form submission
 *     }
 *   );
 *
 *   return (
 *     <form ref={form} onSubmit={handleSubmit} onReset={handleReset}>
 *     </form>
 *   );
 * };
 * ```
 */
export const useInternalForm = (
    id: string,
    onChange: ((name: string, value: FormValue) => void) | undefined,
    onError: () => void,
    onReset: FormEventHandler<HTMLFormElement>,
    onSubmit: HandleFormSubmit
) => {
    const globalFormData = useContext(FormsDataContext);
    const form = useRef<HTMLFormElement>(null);
    const formFieldState = useLocalObservable<FormState>(() => ({
        errors: {},
        fields: {},
        /**
         * Whether the form has errors.
         *
         * @returns A boolean indicating whether the form has errors.
         */
        get hasErrors() {
            return Object.values(this.errors).some(error => error !== null);
        },
        initialValues: {},
        validations: {},
        values: {},
        /**
         * Add a form field to the form.
         *
         * @param name         The name of the form field.
         * @param defaultValue The default value of the form field.
         * @param initialValue The initial value of the form field.
         * @param validation   The validation function for the form field.
         */
        addField(
            name: string,
            defaultValue: FormValue,
            initialValue: FormValue,
            validation: ValidationFunc | undefined
        ) {
            action(() => {
                if (!(name in this.values) && typeof this.values[String(name)] === 'undefined') {
                    this.values[String(name)] = defaultValue;
                }
                if (!(name in this.initialValues) && typeof this.initialValues[String(name)] === 'undefined') {
                    this.initialValues[String(name)] = initialValue;
                }
                if (!(name in this.errors) && typeof this.errors[String(name)] === 'undefined') {
                    this.errors[String(name)] = null;
                }
                if (!(name in this.validations) && typeof this.validations[String(name)] === 'undefined') {
                    this.validations[String(name)] = validation;
                }
            })();
        },
        /**
         * Registers a field reference in the form fields collection using the provided field name and reference object.
         * This method allows dynamically adding field references to the form state, enabling access to field properties and methods outside of the normal render flow. The field reference is stored in an internal collection, keyed by the field name, facilitating easy retrieval and manipulation of field instances. This mechanism is crucial for managing complex forms where fields may need to be accessed or updated programmatically based on user interactions or other conditions.
         *
         * @param name     The name of the field to register, acting as a unique identifier within the form.
         * @param fieldRef The reference object pointing to the field instance, used to access the field's current state and methods.
         */
        addFieldRef(name: string, fieldRef: RefObject<FormField | null>) {
            this.fields[String(name)] = fieldRef;
        },
        /**
         * Changes the validation function for a specific form field.
         *
         * @param name       The name of the form field.
         * @param validation The new validation function for the form field.
         */
        changeValidation(name: string, validation: ValidationFunc | undefined) {
            this.validations[String(name)] = validation;
        },
        /**
         * Change form field values.
         *
         * @param name  The name of the form field.
         * @param value The new value of the form field.
         */
        changeValue(name: string, value: FormValue) {
            const validation = this.validations[String(name)]?.(value, this.values);

            if (validation) {
                this.errors[String(name)] = validation;
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                this.fields[String(name)]?.current?.setCustomValidity('Invalid field');
            } else {
                this.errors[String(name)] = null;
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                this.fields[String(name)]?.current?.setCustomValidity('');
            }

            this.values[String(name)] = value;
            onChange?.(name, value);
        },
        /**
         * Deletes values from the form.
         *
         * @param name The name of the form field.
         */
        deleteValue(name: string) {
            delete this.values[String(name)];
            delete this.initialValues[String(name)];
            delete this.errors[String(name)];
            delete this.validations[String(name)];
        },
        /**
         * Resets an specific form field value.
         *
         * @param name The name of the form field.
         */
        resetValue(name: string) {
            if (name in this.values && typeof this.values[String(name)] !== 'undefined') {
                this.values[String(name)] = this.initialValues[String(name)];
                this.errors[String(name)] = null;
            }
        },
        /**
         * Change form field values.
         */
        resetValues() {
            Object.entries(this.initialValues).forEach(([key, objValue]) => {
                this.values[String(key)] = objValue;
            });
            Object.entries(this.errors).forEach(([key]) => {
                this.errors[String(key)] = null;
            });
        },
        /**
         * Validate the form fields.
         */
        validateFields() {
            Object.entries(this.values).forEach(([key, value]) => {
                const validation = this.validations[String(key)]?.(value, this.values);

                if (validation) {
                    this.errors[String(key)] = validation;
                } else {
                    this.errors[String(key)] = null;
                }
            });
        }
    }));

    /**
     * Handle form reset.
     *
     * This function is called when the form is reset. It restores the form field values
     * to their initial values and calls the `onReset` callback.
     *
     * @param e The form event object.
     */
    const handleReset: FormEventHandler<HTMLFormElement> = e => {
        e.preventDefault();
        e.stopPropagation();

        formFieldState.resetValues();

        if (form.current) {
            form.current.reset();
        }

        onReset(e);
    };

    /**
     * Handle form submission.
     *
     * This function is called when the form is submitted. It prevents the default form submission,
     * stops the event propagation, and calls the `onSubmit` callback with the form values and a
     * `getForm` method to retrieve the form data object.
     *
     * @param e The form event object.
     */
    const handleSubmit: FormEventHandler<HTMLFormElement> = async e => {
        e.preventDefault();
        e.stopPropagation();

        formFieldState.validateFields();

        if (formFieldState.hasErrors) {
            await onError();
            return;
        }

        await onSubmit({
            ctx: formFieldState,
            values: formFieldState.values,
            /**
             * Gets the form data object.
             *
             * @returns The form data object.
             */
            getForm() {
                return new FormData(form.current!);
            }
        });
    };

    const formState = {
        form: formFieldState,
        formId: id
    };

    useEffect(() => {
        if (globalFormData) {
            globalFormData.setFormData(id, formFieldState);
        }

        return () => {
            if (globalFormData) {
                globalFormData.deleteFormData(id);
            }
        };
    }, [formFieldState, globalFormData, id]);

    return {
        form,
        formState,
        handleReset,
        handleSubmit
    };
};